import type {
  ExpiryStateValue,
  StockCountKind,
  StockCountStatus,
  StockMovementType,
  StockTransferStatus,
  StockWriteOffStatus,
  WriteOffReason,
} from "@station/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { useFeatures, usePermissions } from "../../lib/session.js";

/**
 * Market yönetim ekranlarının sorguları (Prompt 16 — web, aşama A; aşama B izinleri de buradadır).
 *
 * Okuma `market.read`; transfer ve fire `market.stock.manage`; sayım `market.count.manage`, sayım onayı
 * `market.count.approve` (**step-up**); açılış stoğu ve mal kabul `market.receipt.manage`; ürün – tedarikçi, istasyon
 * ürün ayarı ve depo `market.catalog.manage`; ayarlar `market.settings.manage`; elle gün kapanışı `shifts.day.close`
 * (**step-up**). Aşama B: sipariş `market.purchase.manage`, üçlü eşleştirme `market.invoice.match`, satış belgesi
 * `market.license.manage`, öneriden fiyat talebi `fuel.price.change` (sorgular `purchasing-queries.ts`). Modül
 * `MARKET` bayrağına bağlıdır; kapalıysa API `403 FEATURE_DISABLED` döner ve ekranlar hiç istek atmadan boş durum
 * çizer. İstasyon KAPSAMI sunucudadır: izin olsa da başka istasyonun belgesi 403 döner.
 *
 * **Tazeleme:** market olayları (`market.*`) ve kasa olayları (`pos.*` — kasa satışı stoğu worker'da düşer)
 * modülün tüm önbelleğini geçersiz kılar (`lib/realtime.ts`). Liste ekranları ayrıca pencere odağında yeniden çeker.
 */

export interface MarketPermissions {
  enabled: boolean;
  canRead: boolean;
  canManageCatalog: boolean;
  canManageReceipts: boolean;
  canManageStock: boolean;
  canManageCounts: boolean;
  canApproveCounts: boolean;
  canManageSettings: boolean;
  canCloseDay: boolean;
  /** Aşama B: sipariş önerisi → sipariş, sipariş yaşam döngüsü. */
  canManagePurchases: boolean;
  /** Aşama B: üçlü eşleştirme (aday görme, eşleştirme, geri alma). */
  canMatchInvoices: boolean;
  /** Aşama B: istasyon satış belgeleri (tütün / alkol). */
  canManageLicenses: boolean;
  /** Fiyat önerisinden fiyat talebi: fiyat modülünün yetkisi (`fuel.price.change`). */
  canChangePrices: boolean;
  /** Belge dosyası yükleme (depolama akışı). */
  canUploadFiles: boolean;
}

export function useMarketPermissions(): MarketPermissions {
  const permissions = usePermissions();
  return {
    enabled: useFeatures().has("MARKET"),
    canRead: permissions.has("market.read"),
    canManageCatalog: permissions.has("market.catalog.manage"),
    canManageReceipts: permissions.has("market.receipt.manage"),
    canManageStock: permissions.has("market.stock.manage"),
    canManageCounts: permissions.has("market.count.manage"),
    canApproveCounts: permissions.has("market.count.approve"),
    canManageSettings: permissions.has("market.settings.manage"),
    canCloseDay: permissions.has("shifts.day.close"),
    canManagePurchases: permissions.has("market.purchase.manage"),
    canMatchInvoices: permissions.has("market.invoice.match"),
    canManageLicenses: permissions.has("market.license.manage"),
    canChangePrices: permissions.has("fuel.price.change"),
    canUploadFiles: permissions.has("files.upload"),
  };
}

type Query = Record<string, string | number>;

/** Boş / tanımsız süzgeçleri sorgudan çıkarır; `true` bayrakları API'nin beklediği "true" dizgesine çevirir. */
export function compact(values: Record<string, string | number | boolean | null | undefined>): Query {
  const query: Query = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    query[key] = value === true ? "true" : value;
  }
  return query;
}

// ── Ayarlar ve depolar ───────────────────────────────────────────────────────
export function useMarketSettings(companyId: string, stationId: string | null) {
  const query = { companyId, ...(stationId ? { stationId } : {}) };
  return useQuery({
    queryKey: queryKeys.market(companyId, "settings", query),
    queryFn: async () => (await api.GET("/market/settings", { params: { query } })).data!,
  });
}

export function useWarehouses(
  companyId: string,
  stationId: string | null,
  options: { includeInactive?: boolean; enabled?: boolean } = {},
) {
  const query = compact({ companyId, stationId, includeInactive: options.includeInactive });
  return useQuery({
    queryKey: queryKeys.market(companyId, "warehouses", query),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    queryFn: async () =>
      (await api.GET("/market/warehouses", { params: { query: query as never } })).data!.items,
  });
}

/**
 * Belge oluşturma pencerelerindeki DEPO seçimi: istasyonun varsayılan deposu önce AÇTIRILIR (idempotent
 * `POST /market/warehouses/default`; kasa satışı yapılmamış yeni istasyonda depo henüz yoktur), sonra
 * istasyonun etkin depoları okunur. Okuma iznine açıktır (`market.read`).
 */
export function useStationWarehouses(companyId: string, stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.market(stationId ?? "none", "warehouses", { ensured: true, companyId }),
    enabled: stationId !== null,
    staleTime: 300_000,
    queryFn: async () => {
      await api.POST("/market/warehouses/default", { body: { stationId: stationId! } });
      return (
        await api.GET("/market/warehouses", { params: { query: { companyId, stationId: stationId! } } })
      ).data!.items;
    },
  });
}

// ── Stok bakiyeleri ──────────────────────────────────────────────────────────
export interface StockFilters {
  stationId?: string | undefined;
  warehouseId?: string | undefined;
  categoryId?: string | undefined;
  search?: string | undefined;
  belowMin?: boolean | undefined;
  negative?: boolean | undefined;
  hideZero?: boolean | undefined;
}

export function stockQuery(companyId: string, filters: StockFilters, cursor?: string, limit = 25): Query {
  return compact({
    companyId,
    limit,
    stationId: filters.stationId,
    warehouseId: filters.warehouseId,
    categoryId: filters.categoryId,
    search: filters.search,
    belowMin: filters.belowMin,
    negative: filters.negative,
    includeZero: filters.hideZero ? "false" : undefined,
    cursor,
  });
}

export function useStockBalances(companyId: string, filters: StockFilters, cursor?: string) {
  const query = stockQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.market(companyId, "stock", query),
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/market/stock", { params: { query: query as never } })).data!,
  });
}

// ── Hareketler ───────────────────────────────────────────────────────────────
export interface MovementFilters {
  stationId?: string | undefined;
  productId?: string | undefined;
  movementType?: StockMovementType | undefined;
  sourceDocId?: string | undefined;
  businessDateFrom?: string | undefined;
  businessDateTo?: string | undefined;
}

export function movementsQuery(
  companyId: string,
  filters: MovementFilters,
  cursor?: string,
  limit = 25,
): Query {
  return compact({ companyId, limit, ...filters, cursor });
}

export function useStockMovements(companyId: string, filters: MovementFilters, cursor?: string) {
  const query = movementsQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.market(companyId, "movements", query),
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/market/stock/movements", { params: { query: query as never } })).data!,
  });
}

// ── Partiler / SKT ───────────────────────────────────────────────────────────
export interface LotFilters {
  stationId?: string | undefined;
  warehouseId?: string | undefined;
  productId?: string | undefined;
  expiryState?: ExpiryStateValue | undefined;
  includeEmpty?: boolean | undefined;
}

export function lotsQuery(companyId: string, filters: LotFilters, cursor?: string, limit = 25): Query {
  return compact({ companyId, limit, ...filters, cursor });
}

export function useStockLots(companyId: string, filters: LotFilters, cursor?: string, enabled = true) {
  const query = lotsQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.market(companyId, "lots", query),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/market/stock/lots", { params: { query: query as never } })).data!,
  });
}

// ── Ürün stok kartı ──────────────────────────────────────────────────────────
export function useProductStockCard(productId: string, companyId: string, stationId: string | null) {
  const query = { companyId, ...(stationId ? { stationId } : {}) };
  return useQuery({
    queryKey: queryKeys.market(productId, "stock-card", query),
    refetchOnWindowFocus: true,
    queryFn: async () =>
      (
        await api.GET("/market/products/{id}/stock-card", {
          params: { path: { id: productId }, query },
        })
      ).data!,
  });
}

/** Ürün kartı (ad, kod, barkod) — URL'de yalnızca kimliği taşınan süzgeçlerin etiketi için. */
export function useProductSummary(productId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.products({ id: productId ?? null }),
    enabled: productId !== undefined,
    staleTime: 300_000,
    queryFn: async () => (await api.GET("/products/{id}", { params: { path: { id: productId! } } })).data!,
  });
}

// ── Transfer ─────────────────────────────────────────────────────────────────
export interface TransferFilters {
  stationId?: string | undefined;
  direction?: "OUTGOING" | "INCOMING" | undefined;
  status?: StockTransferStatus | undefined;
}

export function transfersQuery(
  companyId: string,
  filters: TransferFilters,
  cursor?: string,
  limit = 25,
): Query {
  return compact({
    companyId,
    limit,
    stationId: filters.stationId,
    // Yön yalnızca istasyonla anlamlıdır (sunucu da böyle yorumlar).
    direction: filters.stationId ? filters.direction : undefined,
    status: filters.status,
    cursor,
  });
}

export function useTransfers(companyId: string, filters: TransferFilters, cursor?: string) {
  const query = transfersQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.market(companyId, "transfers", query),
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/market/transfers", { params: { query: query as never } })).data!,
  });
}

export function useTransfer(id: string) {
  return useQuery({
    queryKey: queryKeys.market(id, "transfer"),
    queryFn: async () => (await api.GET("/market/transfers/{id}", { params: { path: { id } } })).data!,
  });
}

// ── Fire / imha ──────────────────────────────────────────────────────────────
export interface WriteOffFilters {
  stationId?: string | undefined;
  status?: StockWriteOffStatus | undefined;
  reason?: WriteOffReason | undefined;
}

export function writeOffsQuery(
  companyId: string,
  filters: WriteOffFilters,
  cursor?: string,
  limit = 25,
): Query {
  return compact({ companyId, limit, ...filters, cursor });
}

export function useWriteOffs(companyId: string, filters: WriteOffFilters, cursor?: string) {
  const query = writeOffsQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.market(companyId, "write-offs", query),
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/market/write-offs", { params: { query: query as never } })).data!,
  });
}

export function useWriteOff(id: string) {
  return useQuery({
    queryKey: queryKeys.market(id, "write-off"),
    queryFn: async () => (await api.GET("/market/write-offs/{id}", { params: { path: { id } } })).data!,
  });
}

// ── Sayım ────────────────────────────────────────────────────────────────────
export interface CountFilters {
  stationId?: string | undefined;
  status?: StockCountStatus | undefined;
  kind?: StockCountKind | undefined;
}

export function countsQuery(companyId: string, filters: CountFilters, cursor?: string, limit = 25): Query {
  return compact({ companyId, limit, ...filters, cursor });
}

export function useCounts(companyId: string, filters: CountFilters, cursor?: string) {
  const query = countsQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.market(companyId, "counts", query),
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/market/counts", { params: { query: query as never } })).data!,
  });
}

/**
 * Sayım ayrıntısı. Tablet ekranında okutmalar AYNI anda başka cihazdan da gelebilir (iki kişi sayar): pencere
 * odağında yeniden çekilir; okutma sonrası sayfa kendi önbelleğini yanıttaki sayılanla günceller.
 */
export function useCount(id: string) {
  return useQuery({
    queryKey: queryKeys.market(id, "count"),
    refetchOnWindowFocus: true,
    queryFn: async () => (await api.GET("/market/counts/{id}", { params: { path: { id } } })).data!,
  });
}

export function useCycleSuggestions(warehouseId: string | null, limit: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.market(warehouseId ?? "none", "cycle-suggestions", { limit }),
    enabled: enabled && warehouseId !== null,
    queryFn: async () =>
      (
        await api.GET("/market/counts/cycle-suggestions", {
          params: { query: { warehouseId: warehouseId!, limit } },
        })
      ).data!,
  });
}

// ── Gün kapanışı ─────────────────────────────────────────────────────────────
export interface ClosingFilters {
  stationId?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export function closingsQuery(
  companyId: string,
  filters: ClosingFilters,
  cursor?: string,
  limit = 25,
): Query {
  return compact({ companyId, limit, ...filters, cursor });
}

export function useDailyClosings(companyId: string, filters: ClosingFilters, cursor?: string) {
  const query = closingsQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.market(companyId, "daily-closings", query),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/market/daily-closings", { params: { query: query as never } })).data!,
  });
}

/**
 * Bir market işlemi birden çok ekranı birden değiştirir (stok, hareket, parti, belge, stok kartı) ve onay
 * motoruna talep açabilir (fire, sayım farkı): modülün tüm önbelleği ve onay önbelleği birlikte tazelenir.
 */
export function useRefreshMarket(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.market() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.approvals() }),
    ]);
  };
}
